import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Spinner, Badge, Modal } from 'react-bootstrap';
import MerchantNavigation from './MerchantNavigation';

const MerchantOrders = () => {
    const { merchant } = useParams();
    const navigate = useNavigate(); // eslint-disable-line no-unused-vars

    // State for orders management
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // State for order details modal
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState(null);
    const [modalData, setModalData] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState({ id: null });

    const API_BASE_URL = 'https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/integration/orders';

    const fetchOrders = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE_URL}?status=ACTIVE`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setOrders(data.data || []);
            } else {
                throw new Error(data.message || 'Failed to fetch orders');
            }
        } catch (err) {
            console.error('Error fetching orders:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const getStatusBadge = (status) => {
        const statusConfig = {
            'PENDING': { variant: 'warning', text: 'Pending' },
            'PROCESSING': { variant: 'info', text: 'Processing' },
            'SHIPPED': { variant: 'primary', text: 'Shipped' },
            'RECEIVED_IN_WAREHOUSE': { variant: 'success', text: 'In Warehouse' },
            'DELIVERED': { variant: 'primary', text: 'Delivered' },
            'CANCELLED': { variant: 'danger', text: 'Cancelled' }
        };

        const config = statusConfig[status] || { variant: 'secondary', text: status };
        return <Badge bg={config.variant} style={{ fontSize: '0.8em' }}>{config.text}</Badge>;
    };

    const getMeliStatusBadge = (status) => {
        const statusConfig = {
            'shipped-dropped_off': { variant: 'success', text: 'Dropped Off', textColor: 'white' },
            'ready_to_ship-printed': { variant: 'warning', text: 'Printed', textColor: 'black' },

        };
        const config = statusConfig[status] || { variant: 'secondary', text: status };
        return <Badge bg={config.variant} style={{ fontSize: '1.0em', color: config.textColor }}>{config.text}</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const handleCloseOrderModal = () => {
        setShowOrderModal(false);
        setModalData(null);
        setModalError(null);
        setSelectedOrder({ id: null });
    };

    // Fetch order details from our API (includes optional madeluxe_data)
    const fetchOrderDetails = async (orderId) => {
        setModalLoading(true);
        setModalError(null);
        setModalData(null);

        try {
            const url = `${API_BASE_URL}/${orderId}?include_madeluxe_data=1`;
            const resp = await fetch(url, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!resp.ok) {
                const text = await resp.text().catch(() => '');
                throw new Error(`HTTP ${resp.status} ${resp.statusText}${text ? ` - ${text}` : ''}`);
            }

            const data = await resp.json();
            setModalData(data);
        } catch (e) {
            console.error('Error fetching order details:', e);
            setModalError(e.message || 'Failed to fetch order details');
        } finally {
            setModalLoading(false);
        }
    };

    const handleViewOrder = (orderId) => {
        setSelectedOrder({ id: orderId });
        setShowOrderModal(true);
        if (orderId) fetchOrderDetails(orderId);
    };

    const handleRefresh = () => {
        fetchOrders();
    };

    const merchantInfo = {
        madeluxe: {
            name: 'Madeluxe',
            description: 'Fashion and accessories merchant',
            color: 'primary'
        },
        pawesome: {
            name: 'Pawesome',
            description: 'Pet supplies and accessories merchant',
            color: 'success'
        }
    };

    const currentMerchant = merchantInfo[merchant] || {
        name: merchant,
        description: 'Merchant dashboard',
        color: 'secondary'
    };

    return (
        <>
            <MerchantNavigation currentPage="orders" />

            <Container className="py-5">
                <Row className="mb-4">
                    <Col>
                        <div className="text-center">
                            <h2 className="mb-3">
                                {currentMerchant.name} - Orders
                            </h2>
                        </div>
                    </Col>
                </Row>

                {error && (
                    <Row className="mb-4">
                        <Col>
                            <Alert variant="danger">
                                <Alert.Heading>Error</Alert.Heading>
                                <p>{error}</p>
                                <Button variant="outline-danger" onClick={handleRefresh}>
                                    Try Again
                                </Button>
                            </Alert>
                        </Col>
                    </Row>
                )}

                <Row>
                    <Col>
                        <Card className="shadow-sm">
                            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="mb-0">Orders ({orders.length})</h5>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={handleRefresh}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Refreshing...
                                            </>
                                        ) : (
                                            <>
                                                🔄 Refresh
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" />
                                        <p className="mt-2">Loading orders...</p>
                                    </div>
                                ) : orders.length === 0 ? (
                                    <div className="text-center py-4">
                                        <p className="text-muted">No orders found</p>
                                        <Button variant="primary" onClick={handleRefresh}>
                                            Refresh
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        {/* Desktop Table View - Hidden on mobile */}
                                        <div className="d-none d-lg-block">
                                            <div className="table-responsive">
                                                <table className="table table-striped table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>Meli Order ID</th>
                                                            <th>Customer</th>
                                                            <th>Product</th>
                                                            <th>Status</th>
                                                            <th>Meli Status</th>
                                                            <th></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {orders.map((order, index) => (
                                                            <tr key={order.meli_order_id || index}>
                                                                <td>
                                                                    <div>
                                                                        <div className="text-muted small">
                                                                            {formatDate(order.received_dateTime)}
                                                                        </div>
                                                                        <div className="fw-bold">
                                                                            <code>{order.meli_order_id}</code>
                                                                        </div>
                                                                        {order.meli_pack_id && (
                                                                            <div className="text-muted small">
                                                                                <code>{order.meli_pack_id}</code>
                                                                            </div>
                                                                        )}

                                                                        {order.order_gsx && (
                                                                            <div>
                                                                                <code>{order.order_gsx}</code>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="mt-2">
                                                                        <Button
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            onClick={() => handleViewOrder(order.meli_order_id)}
                                                                        >
                                                                            Details
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div>
                                                                        <div className="fw-bold">
                                                                            {order.firstname} {order.lastname}
                                                                        </div>
                                                                        <div className="text-muted small">
                                                                            @{order.nickname}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div>
                                                                        <div className="fw-bold">
                                                                            <code>{order.sku}</code>
                                                                        </div>
                                                                        <div className="text-muted small" style={{ maxWidth: '200px' }}>
                                                                            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                                                {order.title}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div
                                                                        className='d-flex flex-column gap-1'
                                                                    >
                                                                        <div className="text-center text-muted small">
                                                                            &nbsp;
                                                                        </div>
                                                                        {getStatusBadge(order.fulfillment_status)}

                                                                        {order.tracking_number ? (
                                                                            <div className="text-muted">
                                                                                <code>{order.tracking_number}</code>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-center text-muted small">
                                                                                No tracking
                                                                            </div>
                                                                        )}

                                                                    </div>
                                                                </td>
                                                                <td
                                                                    className='text-center'
                                                                >
                                                                    {getMeliStatusBadge(order.meli_status)}
                                                                </td>

                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* Mobile Card View - Hidden on desktop */}
                                        <div className="d-lg-none">
                                            {orders.map((order, index) => (
                                                <Card key={order.meli_order_id || index} className="mb-3">
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <div>
                                                                <h6 className="mb-1">
                                                                    <code>{order.meli_order_id}</code>
                                                                </h6>
                                                                <small className="text-muted">
                                                                    {formatDate(order.received_dateTime)}
                                                                </small>
                                                            </div>
                                                            {getStatusBadge(order.fulfillment_status)}
                                                            {getMeliStatusBadge(order.meli_status)}
                                                        </div>

                                                        {order.meli_pack_id && (
                                                            <div className="mb-2">
                                                                <small className="text-muted">
                                                                    <code>{order.meli_pack_id}</code>
                                                                </small>
                                                            </div>
                                                        )}

                                                        {order.order_gsx && (
                                                            <div className="mb-2">
                                                                <small className="text-muted">
                                                                    GSX-<code>{order.order_gsx}</code>
                                                                </small>
                                                            </div>
                                                        )}

                                                        <div className="mb-2">

                                                            <div>
                                                                {order.firstname} {order.lastname}
                                                            </div>
                                                            <small className="text-muted">@{order.nickname}</small>
                                                        </div>

                                                        <div className="mb-2">

                                                            <div>
                                                                <code>{order.sku}</code>
                                                            </div>
                                                            <div className="text-muted small">
                                                                {order.title}
                                                            </div>
                                                        </div>

                                                        {order.tracking_number && (
                                                            <div className="mb-3">

                                                                <div>
                                                                    <code>Tracking {order.tracking_number}</code>
                                                                </div>
                                                            </div>
                                                        )}

                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleViewOrder(order.meli_order_id)}
                                                            className="w-100"
                                                        >
                                                            Details
                                                        </Button>
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Modal show={showOrderModal} onHide={handleCloseOrderModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{`MELI ${selectedOrder.id}`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalLoading && (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                            <p className="mt-2">Fetching order details...</p>
                        </div>
                    )}
                    {!modalLoading && modalError && (
                        <Alert variant="danger">
                            <div className="mb-2">Error loading order details</div>
                            <code style={{ whiteSpace: 'pre-wrap' }}>{modalError}</code>
                        </Alert>
                    )}
                    {!modalLoading && !modalError && modalData && (
                        <pre style={{ maxHeight: 400, overflow: 'auto', background: '#f8f9fa', padding: 12, borderRadius: 6 }}>
                            {JSON.stringify(modalData, null, 2)}
                        </pre>
                    )}
                    {!modalLoading && !modalError && !modalData && (
                        <div className="text-muted">No data</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseOrderModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default MerchantOrders;